/**
 * Booth telemetry, in two halves.
 *
 * `sendDeviceHeartbeat` is the cheap one: liveness plus current peripheral and
 * resource status, upserted server-side into a single row per device. Safe to
 * call every few minutes.
 *
 * `sendAppSettingsTelemetry` is the expensive one: the full settings snapshot
 * (camera, printer, secondary/tertiary printer configs). Each call INSERTS a new
 * document, so it runs on View mount, on settings save, and otherwise rarely.
 *
 * They used to be one call, which forced a compromise: the payload was too heavy
 * to send often, so the booth reported hourly while the dashboard marked a device
 * offline after ten minutes — every healthy booth showed as Critical.
 */
import { useAuthStore } from '@renderer/stores/authStore'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useCreditsStore } from '@renderer/stores/creditsStore'
import { useSubscriptionStore } from '@renderer/stores/subscriptionStore'
import { usePrintStore } from '@renderer/stores/printStore'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useCameraStore } from '@renderer/stores/cameraStore'
import { useOfflineStore } from '@renderer/stores/offlineStore'
import { hasElectronPreload, isInTauriShell } from '@renderer/lib/runtime'
import { usePaperCounterStore } from '@renderer/stores/paperCounterStore'
import { useConfigStore } from '@renderer/stores/configState'
import { useBoothEventStore } from '@renderer/stores/boothEventStore'
import { useFeatureFlagsStore } from '@renderer/stores/featureFlagsStore'
import {
  FLASH_CAPTURE_RETRY_FLAG,
  FLASH_SETUP_GUIDE_FLAG
} from '@renderer/utils/cameras/flashFlags'
import { getCaptureStatsSnapshot } from '@renderer/utils/cameras/captureStats'
import { normaliseFocusMode, WAIT_FOR_FOCUS_DEFAULT } from '@renderer/utils/cameras/prefocus'
import { parseCaptureFormat, resolveCaptureFormat } from '@renderer/utils/captureEncoding'
import { cameraEngineBreakerSummary } from '@renderer/stores/cameraEngineBreakerStore'
import { NativeCamera } from '@renderer/lib/native-gphoto2/nativeCamera'
import { readJsHeap } from '@renderer/utils/telemetry/rendererLifecycle'

type SyncEngineState = 'idle' | 'syncing' | 'retrying' | 'paused'

function deriveTelemetrySyncStatus(
  state: SyncEngineState,
  stats: { pending: number; processing: number; failed: number; total: number }
): string {
  // Prefer engine state over queue counts when active or user-paused
  if (state === 'paused') return 'paused'
  if (state === 'syncing' || state === 'retrying') return 'syncing'
  if (stats.failed > 0) return 'failed'
  if (stats.pending > 0 || stats.processing > 0) return 'pending'
  return 'synced'
}

export interface SendAppSettingsTelemetryOptions {
  projectId?: string | null
  latitude?: number
  longitude?: number
  pin?: string
  pinStrength?: 'weak' | 'strong'
}

/**
 * How often the booth pings. Reported to the server, which derives its
 * offline-detection window from it (roughly 2x this plus a margin) — so changing
 * this number alone is enough to retune detection, no server release required.
 */
export const HEARTBEAT_INTERVAL_MINUTES = 5

function resolvePlatform(): string {
  return hasElectronPreload() ? 'electron' : isInTauriShell() ? 'tauri' : 'web'
}

interface SystemMetricsReading {
  cpuUsagePercent: number | null
  ramUsagePercent: number | null
  diskFreeGB: number | null
  diskDirectory: string | null
  internetStatus: string | null
  /** Only from shells whose main reports process memory; older ones omit them. */
  rendererPrivateMB?: number | null
  appPrivateMB?: number | null
  mainUptimeSec?: number | null
}

interface CollectedDeviceStatus {
  systemMetrics: SystemMetricsReading
  peripheralStatus: {
    camera: string | null
    printer: string | null
    monitor: string | null
  }
  syncStatus: string | undefined
}

/** Peripheral + resource status, shared by both payloads. */
async function collectDeviceStatus(projectId?: string | null): Promise<CollectedDeviceStatus> {
  const settingsStore = useSettingsStore.getState()
  const printStore = usePrintStore.getState()

  let systemMetrics: SystemMetricsReading = {
    cpuUsagePercent: null,
    ramUsagePercent: null,
    diskFreeGB: null,
    diskDirectory: null,
    internetStatus: null
  }
  let peripheralStatus = {
    camera: null as string | null,
    printer: null as string | null,
    monitor: null as string | null
  }

  try {
    if (window.api?.getTelemetrySystemMetrics) {
      systemMetrics = await window.api.getTelemetrySystemMetrics()
    }
  } catch (err) {
    console.warn('[TELEMETRY] Failed to get system metrics:', err)
  }

  try {
    if (window.api?.getTelemetryPeripheralStatus) {
      const selectedPrinter = printStore.selectedPrinter || settingsStore.selectedPrinter || undefined
      peripheralStatus = await window.api.getTelemetryPeripheralStatus(selectedPrinter)

      // Booth telemetry: sample printer CONNECTIVITY (not print jobs — those
      // stay in PrintEventLog). Only when a printer is configured; "no printer
      // selected" is not an outage. The store debounces + measures downtime.
      if (selectedPrinter) {
        if (projectId) useBoothEventStore.getState().setProjectContext(projectId)
        // A healthy printer reports 'connected' — 'ready' is a CAMERA-only status that never
        // occurs for a printer (see PeripheralStatus in main/utils/telemetryPeripherals.ts:
        // printer is 'connected' | 'offline' | 'disconnected' | 'error' | null). Comparing to
        // 'ready' made setPrinterConnected() perpetually false, so every printer_incident
        // opened and NONE ever resolved — inflating printer-downtime metrics while the printer
        // printed fine. Feed a definite up/down edge only; skip null (probe failed) so a
        // transient status-read error can't manufacture a phantom incident.
        if (peripheralStatus.printer != null) {
          useBoothEventStore.getState().setPrinterConnected(peripheralStatus.printer === 'connected', {
            // 'offline' | 'disconnected' | 'error': what the alert says is wrong.
            status: peripheralStatus.printer,
            printerName: selectedPrinter
          })
        }
      }
    }
  } catch (err) {
    console.warn('[TELEMETRY] Failed to get peripheral status:', err)
  }

  if (systemMetrics.internetStatus == null) {
    try {
      if (window.api?.getInternetStatus) {
        const isConnected = await window.api.getInternetStatus()
        systemMetrics.internetStatus = isConnected ? 'connected' : 'disconnected'
      }
    } catch {
      // ignore
    }
  }

  let syncStatus: string | undefined
  try {
    if (window.api?.getSyncState && window.api?.getSyncStats) {
      const [state, stats] = await Promise.all([
        window.api.getSyncState(),
        window.api.getSyncStats()
      ])
      syncStatus = deriveTelemetrySyncStatus(state, stats)
    }
  } catch (err) {
    console.warn('[TELEMETRY] Failed to get sync status:', err)
  }

  return { systemMetrics, peripheralStatus, syncStatus }
}

/**
 * Lightweight liveness ping. Upserts one row per device server-side, so this is
 * cheap enough to run on a short interval.
 */
export async function sendDeviceHeartbeat(projectId?: string | null): Promise<void> {
  const settingsStore = useSettingsStore.getState()
  const deviceId = settingsStore.deviceId
  if (!deviceId) return

  const userEmail = useAuthStore.getState().user?.email ?? ''
  const appVersion = useOfflineStore.getState().appVersion ?? undefined

  // Keep main's copy of the booth identity current. Main sends the shutdown ping
  // on quit, at a point where this renderer no longer exists to be asked, and its
  // background upload jobs use the signed-in account as the owner of last resort
  // for a session whose local row names nobody (main/utils/uploadTarget.ts).
  void window.api?.rememberBoothIdentity?.({
    deviceId,
    projectId: projectId ?? undefined,
    appVersion,
    platform: resolvePlatform(),
    heartbeatIntervalMinutes: HEARTBEAT_INTERVAL_MINUTES,
    accountEmail: userEmail || null
  })

  const { systemMetrics, peripheralStatus, syncStatus } = await collectDeviceStatus(projectId)
  // Renderer memory and uptime: the booth UI dies of memory pressure after hours
  // of running, and the dashboard needs the slope to see it coming. The page's
  // own clock is the renderer's uptime: a crash reload starts it from zero.
  const heap = readJsHeap()

  try {
    const res = await fetch(`${process.env.VITE_API_URL}/api/device-heartbeat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        deviceId,
        userId: userEmail || undefined,
        projectId: projectId ?? undefined,
        appVersion,
        platform: resolvePlatform(),
        heartbeatIntervalMinutes: HEARTBEAT_INTERVAL_MINUTES,
        heartbeatAt: new Date().toISOString(),
        cpuUsagePercent: systemMetrics.cpuUsagePercent,
        ramUsagePercent: systemMetrics.ramUsagePercent,
        diskFreeGB: systemMetrics.diskFreeGB,
        diskDirectory: systemMetrics.diskDirectory,
        internetStatus: systemMetrics.internetStatus,
        syncStatus,
        cameraStatus: peripheralStatus.camera,
        printerStatus: peripheralStatus.printer,
        monitorStatus: peripheralStatus.monitor,
        authStatus: userEmail ? 'logged_in' : 'logged_out',
        totalPaperUsed: usePaperCounterStore.getState().totalPaperUsed,
        isPrintingEnabled: usePrintStore.getState().isPrintingEnabled ?? false,
        rendererUptimeSec: Math.round(performance.now() / 1000),
        ...(heap ? { jsHeapUsedMB: heap.usedMB, jsHeapLimitMB: heap.limitMB } : {}),
        ...(systemMetrics.rendererPrivateMB != null
          ? { rendererPrivateMB: systemMetrics.rendererPrivateMB }
          : {}),
        ...(systemMetrics.appPrivateMB != null ? { appPrivateMB: systemMetrics.appPrivateMB } : {}),
        ...(systemMetrics.mainUptimeSec != null
          ? { mainUptimeSec: systemMetrics.mainUptimeSec }
          : {})
      })
    })
    // A rejected heartbeat is a booth that silently drops out of monitoring
    // (the 128-character deviceId cap did exactly that to every legacy install),
    // so say so instead of treating any answer as delivered.
    if (!res.ok) {
      console.warn('[TELEMETRY] Heartbeat rejected:', res.status, await res.text().catch(() => ''))
    }
  } catch (err) {
    // Never block the booth on monitoring.
    console.warn('[TELEMETRY] Heartbeat send failed:', err)
  }
}

/**
 * Renderer-side goodbye, for booths where main cannot send one: the web and
 * Tauri builds, and as a backstop if an Electron window is closed without the
 * app quitting. `keepalive` is what lets the request outlive the document.
 *
 * Same payload as main's ping (src/main/utils/deviceCloseSignal.ts) — the server
 * stamps `closedAt`, so the dashboard shows the booth as Closed rather than
 * raising a Critical nobody needs to act on.
 */
export function sendDeviceCloseSignal(): void {
  const deviceId = useSettingsStore.getState().deviceId
  if (!deviceId) return

  try {
    void fetch(`${process.env.VITE_API_URL}/api/device-heartbeat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      keepalive: true,
      body: JSON.stringify({
        deviceId,
        projectId: useSessionStateStore.getState().projectId || undefined,
        appVersion: useOfflineStore.getState().appVersion ?? undefined,
        platform: resolvePlatform(),
        heartbeatIntervalMinutes: HEARTBEAT_INTERVAL_MINUTES,
        heartbeatAt: new Date().toISOString(),
        lifecycle: 'shutdown',
        closedReason: 'window_closed'
      })
    }).catch(() => {
      // Offline at closing time is ordinary; the dashboard falls back to
      // treating the silence as unexplained, which is the old behaviour.
    })
  } catch {
    // Never let monitoring get in the way of closing the booth.
  }
}

export async function sendAppSettingsTelemetry(
  options: SendAppSettingsTelemetryOptions = {}
): Promise<void> {
  const { projectId, latitude, longitude, pin, pinStrength } = options
  const userEmail = useAuthStore.getState().user?.email ?? ''
  const settingsStore = useSettingsStore.getState()
  const deviceId = settingsStore.deviceId
  if (!deviceId) {
    console.warn('[TELEMETRY] Missing deviceId for telemetry')
    return
  }

  const subscriptionStore = useSubscriptionStore.getState()
  const creditsStore = useCreditsStore.getState()
  const printStore = usePrintStore.getState()
  const sessionStore = useSessionStateStore.getState()
  const cameraStore = useCameraStore.getState()
  const appVersion = useOfflineStore.getState().appVersion ?? 'unknown'
  const paperStore = usePaperCounterStore.getState()
  const projectConfig = useConfigStore.getState().config
  const flagsStore = useFeatureFlagsStore.getState()
  const postSessionAi = projectConfig?.pages?.result?.settings?.postSessionAi

  let selectedCameraLabel: string | null = null

  // Shared with the lightweight heartbeat so both payloads report identically.
  const { systemMetrics, peripheralStatus, syncStatus } = await collectDeviceStatus(projectId)

  // Resolve the camera LABEL only. This deliberately no longer promotes the
  // camera status to 'ready' just because some videoinput exists: on a DSLR
  // booth with any webcam attached (built-in, capture card), that overwrote a
  // genuinely disconnected DSLR with 'ready' and hid the exact fault device
  // monitoring is meant to surface. Report what the probe actually found.
  const selectedCamId = settingsStore.selectedCamera
  if (selectedCamId) {
    try {
      const mediaDevices = await navigator.mediaDevices.enumerateDevices()
      const match = mediaDevices.find(
        (d) => d.kind === 'videoinput' && d.deviceId === selectedCamId
      )
      if (match?.label) {
        selectedCameraLabel = match.label
      }
    } catch {
      // ignore — label stays null; keep existing camera status
    }
  }

  const telemetry = {
    userId: userEmail || undefined,
    deviceId,
    appVersion,
    platform: resolvePlatform(),
    subscription: {
      package: creditsStore.package || subscriptionStore.type || 'free',
      credits: creditsStore.credits,
      isValid: subscriptionStore.isValid || false,
      token: settingsStore.activationCode || undefined
    },
    features: {
      autoStart: settingsStore.autoStart ?? false,
      autoLaunch: settingsStore.autoLaunch ?? false,
      downloadLocal: settingsStore.downloadLocal ?? false,
      isPrintingEnabled: printStore.isPrintingEnabled ?? false
    },
    camera: {
      selectedCamera: settingsStore.selectedCamera || null,
      selectedCameraLabel: selectedCameraLabel,
      deviceType: cameraStore.deviceType || null,
      useShutterCapture: settingsStore.cameraSettings?.useShutterCapture ?? false,
      // Which focus policy the operator chose. Without it the shutter-duration
      // histograms below are uninterpretable across booths: a booth on 'fixed' or
      // 'once-per-session' is SUPPOSED to look faster, and would otherwise be
      // indistinguishable from one where prefocus simply worked well.
      focusMode: normaliseFocusMode(settingsStore.cameraSettings?.focusMode),
      // Reported alongside focusMode because the two only make sense together: focusMode
      // says WHEN the booth focuses, this says whether the shutter waits for it. A booth
      // with slow captures and this on is explained; the same booth with it off is not.
      waitForFocusBeforeCapture:
        settingsStore.cameraSettings?.waitForFocusBeforeCapture ?? WAIT_FOR_FOCUS_DEFAULT,
      // Flash rollout observability: which gates this booth resolved, whether its
      // operator declared flash use, and how its physical-shutter captures actually
      // went this run. Together these let a flagged cohort be compared against an
      // unflagged one on real bookings instead of on field anecdotes.
      autoBrightenLiveViewForFlash:
        settingsStore.cameraSettings?.autoBrightenLiveViewForFlash ?? false,
      flashFlags: {
        captureRetry: flagsStore.getFlag(FLASH_CAPTURE_RETRY_FLAG, false),
        setupGuide: flagsStore.getFlag(FLASH_SETUP_GUIDE_FLAG, false)
      },
      captureStats: getCaptureStatsSnapshot(),
      // Which camera engine the booth is actually running — 'native' only when the
      // native_gphoto2 flag resolved AND the sidecar bound the camera. The A/B
      // must compare on this, not on the flag (older mains ignore the flag).
      cameraBackend:
        cameraStore.deviceType === 'camera' && cameraStore.camera
          ? cameraStore.camera instanceof NativeCamera
            ? 'native'
            : cameraStore.camera.variant === 'v2'
              ? 'wasm-v2'
              : 'wasm'
          : null,
      // Which camera bodies the circuit breaker has dropped from v2 to v1 on this booth
      // (live trips on the current v2 build only). The fleet view reads this to show
      // where v2 is failing, per body, without anyone toggling a flag.
      cameraEngineBreaker: cameraEngineBreakerSummary(),
      captureQuality: {
        maxWidth: settingsStore.cameraSettings?.captureQuality?.maxWidth || 1920,
        maxHeight: settingsStore.cameraSettings?.captureQuality?.maxHeight || 1080,
        quality: settingsStore.cameraSettings?.captureQuality?.quality || 1.0
      },
      // With the resolution above, what each photo weighs in the upload queue: a PNG
      // booth on venue Wi-Fi is the first suspect when its uploads lag behind.
      captureFormat: resolveCaptureFormat(settingsStore.cameraSettings?.captureFormat),
      // Whether that format is the operator's own pick or the booth default (and so the
      // capture_jpeg_default flag's doing). Without it, "does anyone use the setting?"
      // and "what did the rollout change?" read the same.
      captureFormatChosen:
        parseCaptureFormat(settingsStore.cameraSettings?.captureFormat) !== undefined,
      previewQuality: settingsStore.cameraSettings?.previewQuality
        ? {
          maxWidth: settingsStore.cameraSettings.previewQuality.maxWidth || 1280,
          maxHeight: settingsStore.cameraSettings.previewQuality.maxHeight || 720,
          quality: settingsStore.cameraSettings.previewQuality.quality || 0.8,
          resizeQuality:
            settingsStore.cameraSettings.previewQuality.resizeQuality || 'medium'
        }
        : {
          maxWidth: 1280,
          maxHeight: 720,
          quality: 0.8,
          resizeQuality: 'medium' as const
        },
      captureRatio: settingsStore.cameraSettings?.captureRatio ?? undefined,
      mirror: settingsStore.cameraSettings?.mirror ?? false,
      applyFilterOnPreview: settingsStore.cameraSettings?.applyFilterOnPreview ?? false,
      previewRotation: settingsStore.cameraSettings?.previewRotation ?? 0,
      captureRotation: settingsStore.cameraSettings?.captureRotation ?? 0,
      selectedSecondaryCamera: settingsStore.selectedSecondaryCamera || null,
      secondaryCameraSettings: settingsStore.secondaryCameraSettings
        ? {
          captureRatio: settingsStore.secondaryCameraSettings.captureRatio ?? undefined,
          mirror: settingsStore.secondaryCameraSettings.mirror ?? false,
          useShutterCapture:
            settingsStore.secondaryCameraSettings.useShutterCapture ?? false,
          waitForFocusBeforeCapture:
            settingsStore.secondaryCameraSettings.waitForFocusBeforeCapture ??
            WAIT_FOR_FOCUS_DEFAULT,
          applyFilterOnPreview:
            settingsStore.secondaryCameraSettings.applyFilterOnPreview ?? false,
          previewQuality: settingsStore.secondaryCameraSettings.previewQuality
            ? {
              maxWidth:
                settingsStore.secondaryCameraSettings.previewQuality.maxWidth || 1280,
              maxHeight:
                settingsStore.secondaryCameraSettings.previewQuality.maxHeight || 720,
              quality:
                settingsStore.secondaryCameraSettings.previewQuality.quality || 0.8,
              resizeQuality:
                settingsStore.secondaryCameraSettings.previewQuality.resizeQuality ||
                'medium'
            }
            : undefined,
          captureQuality: settingsStore.secondaryCameraSettings.captureQuality
            ? {
              maxWidth:
                settingsStore.secondaryCameraSettings.captureQuality.maxWidth || 1920,
              maxHeight:
                settingsStore.secondaryCameraSettings.captureQuality.maxHeight || 1080,
              quality:
                settingsStore.secondaryCameraSettings.captureQuality.quality || 1.0,
              resizeQuality:
                settingsStore.secondaryCameraSettings.captureQuality.resizeQuality ||
                'high'
            }
            : undefined,
          captureFormat: resolveCaptureFormat(
            settingsStore.secondaryCameraSettings.captureFormat
          ),
          captureFormatChosen:
            parseCaptureFormat(settingsStore.secondaryCameraSettings.captureFormat) !==
            undefined,
          previewRotation: settingsStore.secondaryCameraSettings.previewRotation ?? 0,
          captureRotation: settingsStore.secondaryCameraSettings.captureRotation ?? 0
        }
        : undefined
    },
    printer: {
      selectedPrinter: printStore.selectedPrinter || settingsStore.selectedPrinter || null,
      printerTarget: printStore.selectedPrinterTarget || 'default',
      selectedRemotePrinter: printStore.getSelectedRemotePrinter()
        ? {
          printerName: printStore.getSelectedRemotePrinter()?.printerName,
          machineIdentifier: printStore.getSelectedRemotePrinter()?.machineIdentifier
        }
        : null,
      useCSharpPrinting: printStore.useCSharpPrinting ?? false,
      useSumatraPDF: printStore.useSumatraPDF ?? false,
      settings: {
        orientation: printStore.settings.orientation || 'landscape',
        pageSize: {
          height: printStore.settings.pageSize?.height || 105000,
          width: printStore.settings.pageSize?.width || 156100
        },
        margins: printStore.settings.margins ?? undefined,
        dpi: printStore.settings.dpi ?? undefined,
        scaleFactor: printStore.settings.scaleFactor ?? undefined,
        printBackground: printStore.settings.printBackground ?? undefined,
        printResolutionScale: printStore.settings.printResolutionScale ?? undefined
      },
      secondaryPrinter: printStore.secondaryPrinter ?? undefined,
      tertiaryPrinter: printStore.tertiaryPrinter ?? undefined,
      // Per-project slot bindings (multi-project hub) — lets ops verify that
      // `routingMatch: 'project'` prints went where the operator bound them.
      printerSlotByProject: printStore.printerSlotByProject ?? {}
    },
    session: {
      hasActiveSession: !!sessionStore.id,
      mode: sessionStore.mode || null
    },
    ...(postSessionAi
      ? {
          postSessionAi: {
            enabled: postSessionAi.enabled === true,
            target: postSessionAi.target ?? null
          }
        }
      : {}),
    ...(pin && pinStrength ? { pin, pinStrength } : {}),
    ...(latitude !== undefined && longitude !== undefined ? { latitude, longitude } : {}),
    heartbeatAt: new Date().toISOString(),
    heartbeatIntervalMinutes: HEARTBEAT_INTERVAL_MINUTES,
    cpuUsagePercent: systemMetrics.cpuUsagePercent,
    ramUsagePercent: systemMetrics.ramUsagePercent,
    diskFreeGB: systemMetrics.diskFreeGB,
    diskDirectory: systemMetrics.diskDirectory,
    internetStatus: systemMetrics.internetStatus,
    ...(syncStatus !== undefined ? { syncStatus } : {}),
    cameraStatus: peripheralStatus.camera,
    printerStatus: peripheralStatus.printer,
    monitorStatus: peripheralStatus.monitor,
    authStatus: userEmail ? 'logged_in' : 'logged_out',
    totalPaperUsed: paperStore.totalPaperUsed
  }

  try {
    const response = await fetch(`${process.env.VITE_API_URL}/api/app-settings-telemetry`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        telemetry,
        projectId: projectId ?? null
      })
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.warn('[TELEMETRY] Telemetry failed:', response.status, errorText)
      return
    }

    try {
      const responseData = await response.json()
      if (responseData.success && responseData.telemetryId) {
        useSessionStateStore.getState().setSessionState({
          appSettingsTelemetryId: responseData.telemetryId
        })
      }
    } catch (parseError) {
      console.warn('[TELEMETRY] Failed to parse telemetry response:', parseError)
    }
  } catch (err) {
    console.warn('[TELEMETRY] Heartbeat send failed:', err)
  }
}
